<div class="run-job-dialog py-4 max-h-[90vh]">
  <!-- Header -->
  <div class="flex items-center justify-between mb-6 pb-2">
    <div class="flex items-center gap-3">
      <div class="w-9 h-9 rounded-full flex items-center justify-center text-lg" [class]="
          job?.color ||
          'bg-gradient-to-br from-indigo-100 to-blue-100 dark:from-indigo-900/20 dark:to-indigo-900/20'
        ">
        {{ job?.avatar || '⚙️' }}
      </div>
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ job?.name }}
        </h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {{ getInstanceName() }}
        </p>
      </div>
    </div>
    <button pButton type="button" class="p-button-text p-button-rounded" (click)="onCancel()" [pTooltip]="'common.actions.close' | transloco">
      <i class="fa-light fa-times text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"></i>
    </button>
  </div>

  @if (successMessage || resultEntries.length || resultText) {
  <div class="rounded-lg p-4 space-y-2 mt-4">
    @if (successMessage) {
    <h2 class="text-sm font-medium text-green-900 dark:text-green-200">
      <span class="me-1" aria-hidden="true">🎉</span>
      {{ successMessage }}
    </h2>
    }
    @if (hasProcessedBankCheck && !hasConfirmedSave) {
    <div class="flex flex-wrap gap-2 pt-2 mb-6">
      <button type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
        (click)="onCancel()" [disabled]="isSavingResult">
        {{ 'common.actions.close' | transloco }}
      </button>
      @if (resultForm) {
        @if (isResultEditing) {
        <button type="button"
          class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
          (click)="onCancelResultEdit()" [disabled]="isSavingResult">
          {{ 'jobRunDialog.actions.cancelEdit' | transloco }}
        </button>
        } @else {
        <button type="button"
          class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
          (click)="onEditResult()" [disabled]="isSavingResult">
          {{ 'common.actions.edit' | transloco }}
        </button>
        }
      }
      <button type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-default transition-colors flex items-center gap-2"
        (click)="onConfirmSave()" [disabled]="isSavingResult">
        @if (isSavingResult) {
        <i class="fa fa-spinner fa-spin"></i>
        } @else {
        <i class="fa-light fa-check"></i>
        }
        {{ 'jobRunDialog.actions.confirmAndSave' | transloco }}
      </button>
    </div>
    }
    @if (resultImagePreview) {
      <img [src]="resultImagePreview" alt="Uploaded image preview"
        class="w-full h-auto max-h-72 rounded-md object-contain" />
    }
    @if (resultEntries.length) {
      @if (resultForm) {
      <div [formGroup]="resultForm" class="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-6">
        @for (entry of resultEntries; track entry.key) {
        <div class="space-y-2">
          <p class="text-xs font-semibold uppercase tracking-wide text-neutral-700 dark:text-neutral-300">
            {{ entry.displayKey }}
          </p>
          @if (isResultEditing) {
          <textarea [formControlName]="entry.key" rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all resize-none"></textarea>
          } @else {
          <p class="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
            {{ entry.value || '—' }}
          </p>
          }
        </div>
        }
      </div>
      } @else {
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        @for (entry of resultEntries; track entry.key) {
          <p class="text-xs font-semibold uppercase tracking-wide text-neutral-700 dark:text-neutral-300 mb-1">
            {{ entry.displayKey }}
          </p>
          <p class="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
            {{ entry.value || '—' }}
          </p>
        }
      </div>
      }
    }
    @if (resultText) {
    <p class="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
      {{ resultText }}
    </p>
    }
  </div>
  }@else {
  <p class="mb-4">{{instance?.description}}</p>

  @if (isLoading || !runForm) {
  <div class="flex items-center justify-center py-12">
    <div class="text-center">
      <i class="fa fa-spinner fa-spin text-3xl text-gray-400 mb-3"></i>
      <p class="text-sm text-gray-600 dark:text-gray-400">{{ 'common.state.loading' | transloco }}</p>
    </div>
  </div>
  } @else {
  <form [formGroup]="runForm" class="space-y-5">

    <!-- Variable Parameters Section -->
    @if (getVariableParameters().length > 0) {
    <div class="space-y-4">
      <div formGroupName="variableParameters" class="space-y-4">
        @for (param of getVariableParameters(); track param.uuid) {
        <div class="space-y-2">
          <!-- Text Input -->
          @if (param.type === 'text-input') {
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ param.label }}
            @if (param.required) {
            <span class="text-red-500">*</span>
            }
          </label>
          <input type="text" [formControlName]="param.variable" [placeholder]="param.description || param.label"
            class="w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
            [maxlength]="param.max_length"
            [class.border-red-300]="getVariableParameterFormControl(param)?.invalid && getVariableParameterFormControl(param)?.touched" />
          @if (param.description) {
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ param.description }}</p>
          }
          @if (
          getVariableParameterFormControl(param)?.invalid &&
          getVariableParameterFormControl(param)?.touched
          ) {
          <p class="text-xs text-red-600 dark:text-red-400 mt-1">{{ 'jobRunDialog.validation.fieldRequired' | transloco: { field: param.label } }}</p>
          }
          }

          <!-- Paragraph/Textarea -->
          @if (param.type === 'textarea') {
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ param.label }}
            @if (param.required) {
            <span class="text-red-500">*</span>
            }
          </label>
          <textarea [formControlName]="param.variable" [placeholder]="param.description || param.label" rows="3"
            class="w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all resize-none"
            [maxlength]="param.max_length"
            [class.border-red-300]="getVariableParameterFormControl(param)?.invalid && getVariableParameterFormControl(param)?.touched"></textarea>
          @if (param.description) {
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ param.description }}</p>
          }
          @if (
          getVariableParameterFormControl(param)?.invalid &&
          getVariableParameterFormControl(param)?.touched
          ) {
          <p class="text-xs text-red-600 dark:text-red-400 mt-1">{{ 'jobRunDialog.validation.fieldRequired' | transloco: { field: param.label } }}</p>
          }
          }

          <!-- File Upload -->
          @if (param.type === 'file-upload') {
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ param.label }}
            @if (param.required) {
            <span class="text-red-500">*</span>
            }
          </label>
          <div class="space-y-2">
            @if (getUploadedFileName(param)) {
            <!-- File Uploaded State -->
            <div class="space-y-3">
              <!-- Image Preview -->
              @if (getImagePreview(param) && isImageFile(getUploadedFileName(param) || '')) {
              <div
                class="relative w-full rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                <img [src]="getImagePreview(param)" [alt]="getUploadedFileName(param) || 'Preview'"
                  class="w-full h-auto max-h-64 object-contain" (error)="$event.target.style.display='none'" />
              </div>
              }

              <!-- File Info -->
              <div
                class="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div class="flex items-center gap-2 flex-1 min-w-0">
                  <i class="fa-light fa-check-circle text-green-600 dark:text-green-400"></i>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-green-900 dark:text-green-100 truncate">
                      {{ getUploadedFileName(param) }}
                    </p>
                    <p class="text-xs text-green-600 dark:text-green-400">
                      @if (getUploadedFileSize(param)) {
                      {{ getFileSizeDisplay(getUploadedFileSize(param)!) }}
                      }
                    </p>
                  </div>
                </div>
                <button type="button" (click)="removeFile(param, fileInput)"
                  class="ms-2 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                  [pTooltip]="'jobRunDialog.actions.removeFile' | transloco">
                  <i class="fa-light fa-times text-sm"></i>
                </button>
              </div>
            </div>
            <input #fileInput type="file" accept="image/*" (change)="onFileSelected($event, param)" class="hidden" />
            } @else {
            <!-- File Upload Input -->
            <div class="relative">
              <input #fileInput type="file" accept="image/*" (change)="onFileSelected($event, param)" class="hidden"
                [id]="'file-input-' + param.variable" />
              <label [for]="'file-input-' + param.variable"
                class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg cursor-pointer bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
                (dragover)="onFileDragOver($event)"
                (drop)="onFileDrop($event, param, fileInput)"
                [class.border-red-300]="getVariableParameterFormControl(param)?.invalid && getVariableParameterFormControl(param)?.touched">
                <div class="flex flex-col items-center justify-center pt-5 pb-6">
                  <i class="fa-light fa-cloud-arrow-up text-3xl text-gray-400 dark:text-gray-500 mb-2"></i>
                  <p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span class="font-semibold">{{ 'jobRunDialog.upload.clickToUpload' | transloco }}</span>
                    {{ 'jobRunDialog.upload.orDragAndDrop' | transloco }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ 'jobRunDialog.upload.imageOnlyHint' | transloco }}</p>
                </div>
              </label>
            </div>
            }
            <!-- @if (param.description) {
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ param.description }}</p>
            } -->
            @if (
            getVariableParameterFormControl(param)?.invalid &&
            getVariableParameterFormControl(param)?.touched
            ) {
            <p class="text-xs text-red-600 dark:text-red-400">{{ 'jobRunDialog.validation.fieldRequired' | transloco: { field: param.label } }}</p>
            }
          </div>
          }

          <!-- Number Input -->
          @if (param.type === 'number-input') {
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ param.label }}
            @if (param.required) {
            <span class="text-red-500">*</span>
            }
          </label>
          <input type="number" [formControlName]="param.variable" [placeholder]="param.description || param.label"
            class="w-full px-3 py-2 text-sm rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
            [class.border-red-300]="getVariableParameterFormControl(param)?.invalid && getVariableParameterFormControl(param)?.touched" />
          @if (param.description) {
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ param.description }}</p>
          }
          @if (
          getVariableParameterFormControl(param)?.invalid &&
          getVariableParameterFormControl(param)?.touched
          ) {
          <p class="text-xs text-red-600 dark:text-red-400 mt-1">{{ 'jobRunDialog.validation.fieldRequired' | transloco: { field: param.label } }}</p>
          }
          }

          <!-- Select -->
          @if (param.type === 'select' && param.options && param.options.length > 0) {
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ param.label }}
            @if (param.required) {
            <span class="text-red-500">*</span>
            }
          </label>
          <p-select [formControlName]="param.variable" [options]="param.options"
            [placeholder]="'Select ' + param.label.toLowerCase()" class="w-full" styleClass="text-sm"></p-select>
          @if (param.description) {
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ param.description }}</p>
          }
          @if (
          getVariableParameterFormControl(param)?.invalid &&
          getVariableParameterFormControl(param)?.touched
          ) {
          <p class="text-xs text-red-600 dark:text-red-400 mt-1">{{ 'jobRunDialog.validation.fieldRequired' | transloco: { field: param.label } }}</p>
          }
          }

          <!-- Checkbox -->
          @if (param.type === 'checkbox') {
          <div class="flex items-start gap-3">
            <p-checkbox [formControlName]="param.variable" [inputId]="param.variable" [binary]="true"
              styleClass="mt-0.5"></p-checkbox>
            <div class="flex-1">
              <label [for]="param.variable"
                class="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer block">
                {{ param.label }}
                @if (param.required) {
                <span class="text-red-500">*</span>
                }
              </label>
              @if (param.description) {
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ param.description }}</p>
              }
              @if (
              getVariableParameterFormControl(param)?.invalid &&
              getVariableParameterFormControl(param)?.touched
              ) {
              <p class="text-xs text-red-600 dark:text-red-400 mt-1">{{ 'jobRunDialog.validation.fieldRequired' | transloco: { field: param.label } }}</p>
              }
            </div>
          </div>
          }
        </div>
        }
      </div>
    </div>
    } @else {
    <div class="text-center py-8">
      <i class="fa-light fa-inbox text-3xl text-gray-400 mb-3"></i>
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ 'jobRunDialog.empty.noVariableParameters' | transloco }}
      </p>
    </div>
    }

    <!-- Action Buttons -->
    <div class="flex justify-end gap-2 pt-4">
      <button type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg border border-neutral-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
        (click)="onCancel()" [disabled]="isRunning">
        {{ 'common.actions.cancel' | transloco }}
      </button>
      <button type="button"
        class="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-default transition-colors flex items-center gap-2"
        (click)="onRun()" [disabled]="runForm.invalid || isRunning">
        @if (isRunning) {
        <i class="fa fa-spinner fa-spin"></i>
        } @else {
        <i class="fa-light fa-play"></i>
        }
        {{ 'jobRunDialog.actions.runJob' | transloco }}
      </button>
    </div>
  </form>
  }
  }
</div>